import { useState, useMemo, useRef, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import moment from 'moment';
import _ from 'lodash';

import Utils from '../../../utils/utils.js';
import { useNotifications } from '../NotificationsContext.js';

import SearchBar from '../../controls/SearchBar.jsx';
import List from '../../controls/List.jsx';
import Toolbar from '../../controls/Toolbar.jsx';
import NotificationDetails from './NotificationDetails.jsx';
import FilterHeader from '../FilterHeader.jsx';
import DATE_VARIALBES from '../../../config/dateVariables.js';

const ManageNotifications = () => {
  const { objectType, id, objectSubType = 'manage', objectSubTypeId } = useParams();
  const history = useHistory();
  const ref = useRef();
  const [translate] = useTranslation();

  const STATUS_FILTER_OPTIONS = useMemo(() => [
    { value: 'read', displayValue: translate("OTM_NOTIFICATIONS_STATUS_READ") },
    { value: 'sent', displayValue: translate("OTM_NOTIFICATIONS_STATUS_SENT") },
  ], [translate]);
  const baseUrl = `/${objectType}/${id}/${objectSubType}`;

  const { notificationsData } = useNotifications();

  const [filter, setFilter] = useState('90d');

  const [minDate] = useState(new Date(Date.now() - DATE_VARIALBES.NINETY_DAYS_IN_MS));
  const [maxDate] = useState(new Date());
  const [startDate, setStartDate] = useState(minDate);
  const [endDate, setEndDate] = useState(maxDate);
  const [statusFilter, setStatusFilter] = useState([STATUS_FILTER_OPTIONS[0].value, STATUS_FILTER_OPTIONS[1].value]);
  const [searchPrompt, setSearchPrompt] = useState("");

  const filteredData = useMemo(() => {
    const result = [];

    Object.entries(_.cloneDeep(notificationsData)).forEach(([userId, registrations]) => {
      Object.values(registrations).forEach((registration) => {
        let filteredNotifications = registration.notifications.filter(({ updatedAt }) => moment(updatedAt) <= moment(endDate) && moment(updatedAt) >= moment(startDate));

        filteredNotifications = filteredNotifications.filter(({ status }) => statusFilter.includes(status));

        result.push(...filteredNotifications.map((notification) => ({ userId, description: `${userId} | ${notification.payload?.title} | ${Utils.getFormattedDateTime(notification.updatedAt)} | ${registration.userAgent}`, ...notification })))
      });
    });

    return result.filter(({ userId }) => userId.toLowerCase().includes(searchPrompt.toLowerCase())).sort((notificationA, notificationB) => moment(notificationB.updatedAt).valueOf() - moment(notificationA.updatedAt).valueOf());
  }, [searchPrompt, notificationsData, startDate, endDate, statusFilter]);

  useEffect(() => {
    if (!objectSubTypeId && filteredData.length > 0) {
      history.push(`${baseUrl}/${filteredData[0]._id}`)
    }
  }, [objectSubTypeId, baseUrl, history, filteredData]);

  const selectedNotification = useMemo(() => {
    if (objectSubTypeId) {
      return filteredData.find(({ _id }) => _id === objectSubTypeId) || null;
    }

    return null;
  }, [objectSubTypeId, filteredData]);

  return (
    <div className='row h-100'>
      <div className='col-7 h-100'>
        <div className='card h-100'>
          <div className='card-header border-bottom'>
            <Toolbar heading={`${filteredData.length} ${translate('OTM_NOTIFICATIONS')}`} />
          </div>
          <div className='card-body overflow-hidden h-100'>
            <div className='overflow-auto h-100 px-1'>
              <div className="sticky-top bg-white">
                <div className="mb-1">
                  <SearchBar
                    ref={ref}
                    size="sm"
                    initialValue={searchPrompt}
                    placeholder={translate("OTM_SEARCH_USER")}
                    onChange={(value) => setSearchPrompt(value)} />
                </div>

                <FilterHeader
                  filter={filter}
                  setFilter={setFilter}
                  minDate={minDate}
                  maxDate={maxDate}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  statusFilterOptions={STATUS_FILTER_OPTIONS}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                />
              </div>
              <List
                data={filteredData}
                attr='description'
                selectedElement={selectedNotification?._id}
                onClick={(eventKey) => history.push(`${baseUrl}/${eventKey}`)}
                callbacks={[{
                  icon: (i) => filteredData.at(i).status === 'sent' ? 'CircleFill' : 'Circle',
                  variant: 'outline-primary',
                  disabled: true,
                }]}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='col-5 h-100'>
        <div className='overflow-auto h-100 px-1'>
          {selectedNotification && <NotificationDetails {...selectedNotification} />}
        </div>
      </div>
    </div>
  );
};

export default ManageNotifications;